import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const AudiobookProduction = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: 'Professional Voice Acting',
      description: 'Expert voice actors with experience in your genre bring your characters and narrative to life.',
      features: ['Genre-specific talent', 'Multiple voice options', 'Character voices', 'Accent expertise']
    },
    {
      title: 'Audio Engineering',
      description: 'Professional studio recording, editing, and mastering to meet ACX and industry standards.',
      features: ['Studio quality', 'Noise reduction', 'Audio mastering', 'Quality control']
    },
    {
      title: 'ACX Distribution',
      description: 'Complete Audible/Amazon/iTunes distribution setup and ongoing support.',
      features: ['ACX compliance', 'Audible publishing', 'iTunes distribution', 'Retail optimization']
    },
    {
      title: 'Proofreading & QA',
      description: 'Thorough quality assurance to ensure your audiobook is error-free and professionally polished.',
      features: ['Script proofreading', 'Audio QA', 'Pronunciation guide', 'Retakes included']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Script Prep & Voice Casting',
      description: 'We prepare your manuscript for audio, create pronunciation guides, and match you with the perfect voice talent from our roster of professional narrators.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png'
    },
    {
      step: '02',
      title: 'Recording & Production',
      description: 'Professional studio recording with real-time quality control. Our audio engineers ensure broadcast-quality sound that meets all industry standards.',
      icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png'
    },
    {
      step: '03',
      title: 'Mastering & Distribution',
      description: 'Final audio mastering, ACX quality check, and distribution to Audible, Amazon, iTunes, and other major audiobook platforms worldwide.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png',
      title: 'ACX Approved',
      description: 'All our audiobooks meet strict ACX technical requirements and quality standards.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png',
      title: 'Professional Narrators',
      description: 'Access to 100+ experienced voice actors across all genres and accents.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png',
      title: 'Fast Turnaround',
      description: 'Most audiobooks completed within 4-6 weeks from script to distribution.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png',
      title: 'Unlimited Revisions',
      description: 'We work until you\'re completely satisfied with the final audio production.'
    }
  ];

  const faqs = [
    {
      question: 'How much does audiobook production cost?',
      answer: 'Pricing depends on book length and narrator experience. Typical rates are $200-400 per finished hour (PFH). A 60,000-word book is approximately 6-7 hours finished audio, so $1,200-$2,800 total. We offer royalty-share options for qualifying titles where costs are split with the narrator.'
    },
    {
      question: 'How long does audiobook production take?',
      answer: 'Production typically takes 4-6 weeks for a standard-length book. This includes: script preparation (1 week), recording (1-2 weeks), editing and mastering (1-2 weeks), and quality assurance (1 week). Rush production available for urgent projects.'
    },
    {
      question: 'Can I choose my narrator?',
      answer: 'Yes! We provide voice samples from multiple narrators matching your book\'s genre and style. You select your preferred narrator, and we can arrange a paid audition (15-minute sample) before committing to the full production.'
    },
    {
      question: 'What is ACX and why is it important?',
      answer: 'ACX (Audiobook Creation Exchange) is Amazon\'s audiobook production and distribution platform. It connects to Audible, Amazon, and iTunes - the three largest audiobook retailers controlling 90%+ of the market. ACX compliance ensures your audiobook meets technical standards for these platforms.'
    },
    {
      question: 'Do I need the audiobook rights to my book?',
      answer: 'Yes, you must own or control the audiobook rights to produce an audiobook. If you self-published, you typically own all rights. If traditionally published, check your contract - many publishers retain audiobook rights.'
    },
    {
      question: 'What format should my manuscript be in?',
      answer: 'We accept manuscripts in any common format (.docx, .pdf, .txt). We\'ll format it for narration, add pronunciation guides for unusual names/terms, and prepare chapter markers for the final audiobook.'
    },
    {
      question: 'Can I narrate my own audiobook?',
      answer: 'Yes! We offer studio rental and audio engineering services if you want to narrate yourself. This includes recording direction, editing, mastering, and ACX distribution. However, professional narrators typically deliver higher quality that listeners prefer.'
    },
    {
      question: 'What royalties will I earn from my audiobook?',
      answer: 'Through ACX exclusive distribution: 40% royalty. Non-exclusive: 25% royalty. For a $20 audiobook, you\'d earn $8 (exclusive) or $5 (non-exclusive) per sale. You keep 100% of your rights and can leave ACX exclusive after 7 years.'
    }
  ];

  const testimonials = [
    {
      name: 'Patricia Henderson',
      title: 'Mystery Author',
      text: 'The narrator they matched me with perfectly captured my detective character\'s voice. The audiobook sales now rival my ebook sales!',
      rating: 5
    },
    {
      name: 'Marcus Williams',
      title: 'Sci-Fi Author',
      text: 'Professional production from start to finish. The audio quality is outstanding and my audiobook was approved by ACX on first submission.',
      rating: 5
    },
    {
      name: 'Diana Foster',
      title: 'Romance Author',
      text: 'I was nervous about my first audiobook but they guided me through every step. The narrator made my characters come alive beautifully.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PROFESSIONAL AUDIOBOOK PRODUCTION</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Bring Your Book to <span className="text-amber-700">Life with Audio</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional audiobook production with expert narrators and ACX-compliant audio engineering. Expand your reach to millions of audiobook listeners on Audible, Amazon, and iTunes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  Get Free Quote
                </Button>
                <Button 
                  onClick={() => scrollToSection('process')}
                  className="bg-white hover:bg-gray-50 text-amber-700 border-2 border-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  See How It Works
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">200+</div>
                  <div className="text-gray-600 text-sm">Audiobooks Produced</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">100+</div>
                  <div className="text-gray-600 text-sm">Voice Actors</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">100%</div>
                  <div className="text-gray-600 text-sm">ACX Approved</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">4-6</div>
                  <div className="text-gray-600 text-sm">Weeks Average</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800&h=600&fit=crop" 
                  alt="Audiobook production"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img 
                        src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                        alt="Audiobooks produced"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">200+</div>
                      <div className="text-sm text-gray-600">Audiobooks Produced</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 right-12 w-40 h-40 bg-amber-300 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Voice Talent Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800&h=600&fit=crop" 
                alt="Professional voice narrators"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-700/20 to-transparent rounded-2xl"></div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PROFESSIONAL NARRATORS</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Expert Voice Talent for <span className="text-amber-700">Every Genre</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We match you with professional narrators who specialize in your book's genre. All our voice actors have audiobook experience and understand pacing, character voices, and emotional delivery.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                      alt="Genre expertise"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Genre Specialists</h3>
                    <p className="text-gray-600">Fiction, non-fiction, business, memoir, children's - we have narrators for every category.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/2521/2521826.png" 
                      alt="Voice samples"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Listen Before You Choose</h3>
                    <p className="text-gray-600">Review voice samples and select from multiple narrator options before starting production.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/1087/1087927.png" 
                      alt="Accents & styles"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Accents & Character Voices</h3>
                    <p className="text-gray-600">British, Southern, New York, and international accents. Multiple character voices for fiction.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                Hear Voice Samples
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Creative CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Audiobooks Are Growing 25% Yearly
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Don't miss out on the fastest-growing segment of book publishing. Audiobook listeners are highly engaged and willing to pay premium prices.
            </p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Start Your Audiobook
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete <span className="text-amber-700">Audiobook Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need from script to distribution on all major audiobook platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It <span className="text-amber-700">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined production process from manuscript to finished audiobook on all major platforms.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200" style={{ top: '80px' }}></div>

              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-amber-500 relative z-10">
                    <div className="absolute -top-6 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">{item.step}</span>
                      </div>
                    </div>

                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>

                    {index < process.length - 1 && (
                      <div className="hidden md:block absolute -right-4 top-20 text-amber-500">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {index < process.length - 1 && (
                    <div className="md:hidden flex justify-center py-4">
                      <svg className="w-8 h-8 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l3.293-3.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">Ready to create your audiobook?</p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-6 px-12 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Get Free Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WHY CHOOSE US</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-amber-700">Rushmore Publishing</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Industry-leading audiobook production with ACX-approved quality and professional narrators who bring your story to life.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=800&h=600&fit=crop" 
                alt="Professional audiobook studio"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-amber-700 text-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold mb-2">100%</div>
                <div className="text-sm">ACX Approval Rate</div>
                <div className="mt-3 text-xs opacity-90">First-time approval guaranteed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1920&h=800&fit=crop" 
            alt="Start audiobook production"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-8">
              <span className="text-white text-sm font-semibold tracking-wider">FREE VOICE SAMPLES AVAILABLE</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Create Your Audiobook?
            </h2>
            <p className="text-xl mb-12 opacity-90 leading-relaxed">
              Get a free quote and hear voice samples from professional narrators. Let's bring your book to the growing audiobook audience.
            </p>
            
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
            >
              Get Your Free Quote
            </Button>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto pt-12 border-t border-white/20 mt-12">
              <div>
                <div className="text-3xl font-bold mb-2">200+</div>
                <div className="text-sm opacity-80">Audiobooks Produced</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">100+</div>
                <div className="text-sm opacity-80">Voice Actors</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-sm opacity-80">ACX Approved</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-80">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-amber-700">Authors Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-amber-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-amber-700">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all"
                >
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AudiobookProduction;